<template>
  <div class="category-block">
    <div class="section-title">Member Roster Gathering</div>
    
    <div class="roster-table-wrapper">
      <table class="roster-table">
        <thead>
          <tr>
            <th style="width: 20%">Name</th>
            <th style="width: 15%">Gender</th>
            <th v-if="showType" style="width: 30%">Type</th>   <!-- 条件显示 -->
            <th style="width: 10%">Size</th>
            <th style="width: 10%">Quantity</th>
            <th style="width: 15%">Actions</th>
          </tr>
        </thead>

        <tbody v-for="(member, idx) in rosterList" :key="idx">
          <tr class="data-row">
            <td>
              <input 
              v-model="member.name" 
              placeholder="Name" 
              class="roster-input name-input"
              :class="getInputClass(member.name)"
              @input="onFieldChange(member)">
            </td>
            <td>
              <select v-model="member.gender" class="roster-select">
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </td>
            <!-- Type 列：条件显示 -->
            <td v-if="showType">
              <select v-model="member.style" class="roster-select">
                <option value="Short Sleeve">Short sleeve jersey</option>
                <option value="Long Sleeve">Long sleeve jersey</option>
                <option value="Cycling Vest">Sleeveless jersey</option>
                <option value="Mx Jersey">MX jersey</option>
                <option value="Hoodie">Hoodie</option>
                <option value="T-shirt">T-shirt</option>
                <option value="Baseball Jersey">Baseball jersey</option>
              </select>
            </td>
            <td>
              <select 
                v-model="member.size" 
                class="roster-select"
                :class="getInputClass(member.size)"
                @change="onFieldChange(member)"
              >
                <option value="">Size</option>
                <option value="XS">XS</option>
                <option value="S">S</option>
                <option value="M">M</option>
                <option value="L">L</option>
                <option value="XL">XL</option>
                <option value="2XL">2XL</option>
                <option value="3XL">3XL</option>
                <option value="4XL">4XL</option>
              </select>
            </td>
            <td>
              <input 
                type="number" 
                v-model="member.quantity" 
                min="1" 
                class="roster-input quantity-input" 
                placeholder="Qty"
                :class="getInputClass(member.quantity)"
                @input="onFieldChange(member)">
            
            </td>
            <td class="operate-cell">
              <div class="operate-buttons">
                
                <button :data-idx="idx" class="icon-btn danger delete-btn" @click="showDeleteModal(idx)">Delete</button>
              </div>
            </td>
          </tr>
          <tr class="notes-row">
            <td class="notes-placeholder"></td>
            <td :colspan="showType ? 4 : 3">
              <input v-model="member.message" placeholder="Add notes here..." class="roster-input notes-input">
            </td>
          </tr>
        </tbody>

        <tbody v-if="rosterList.length === 0">
          <tr>
            <td colspan="6" class="empty-row">Add your info now!</td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div class="roster-footer">
      <button @click="addMember" class="add-member-btn">+ Add Member</button>
      <div class="roster-stats">
        Total: {{ rosterList.length }} Members / {{ totalQuantity }} pcs
      </div>
    </div>
    
    <div v-if="saveMessage" :class="['save-status', saveMessageType]">
      {{ saveMessage }}
    </div>
  </div>

<!-- 自定义删除确认弹窗 -->
<div id="deleteModal" class="delete-modal" style="display: none;">
    <div class="delete-modal-content">
        <div class="delete-modal-header">
            <h3>Delete Team Member?</h3>
        </div>
        <div class="delete-modal-body">
            <p id="deleteModalMessage">Are you sure you want to remove this member from the roster? This action cannot be undone.</p>
        </div>
        <div class="delete-modal-footer">
            <button id="deleteModalCancelBtn" class="delete-modal-btn cancel">Cancel</button>
            <button id="deleteModalConfirmBtn" class="delete-modal-btn confirm">Delete</button>
        </div>
    </div>
</div>

</template>

<script>
import { useDesignStore } from '@/stores/designStore'
import { onBeforeUnmount } from 'vue'

export default {
  name: 'RosterManager',
  props: {
    showType: {
      type: Boolean,
      default: false   // 默认不显示 Type 列
    }
  },
  setup() {
    const store = useDesignStore()
    return { store }
  },
  data() {
    return {
      rosterList: [],
      saveMessage: '',
      saveMessageType: '',
      _saveTimer: null,  // 防抖定时器
      pendingDeleteIndex: null  // 记录待删除队员的索引
    }
  },
  computed: {
    totalQuantity() {
      return this.rosterList.reduce((sum, m) => sum + (Number(m.quantity) || 0), 0)
    },
    // 将每个队员转换为两行数据
    flattenedRows() {
      const rows = []
      this.rosterList.forEach((member, idx) => {
        // 第一行：数据行
        rows.push({
          type: 'data',
          id: `data-${idx}`,
          member: member,
          index: idx
        })
        // 第二行：备注行
        rows.push({
          type: 'notes',
          id: `notes-${idx}`,
          member: member,
          index: idx
        })
      })
      return rows
    }
  },
  mounted() {
    // 立即加载
    this.loadRosterWithRetry()
  },
  watch: {
      // ★★★ 监听服装款式变化，自动更新所有队员的默认款式 ★★★
      'store.currentGarment': {
        handler(newGarment, oldGarment) {
          if (newGarment && newGarment !== oldGarment) {
            console.log('服装款式切换为:', newGarment)
            // 更新所有现有队员的 style 为新款式
            this.rosterList.forEach(member => {
              // 只有当队员的 style 是旧款式或者是空的时候才更新
              if (!member.style || member.style === oldGarment) {
                member.style = newGarment
              }
            })
            // 触发保存
            if (this.store.currentDesignId) {
              this.saveToServer()
            }
          }
        },
        immediate: false
      },

    // 监听 designId 变化，重新加载队员数据
    // 注意：如果 store.rosterList 已由 RosterPage.init() 提前写入，则跳过网络请求
    'store.currentDesignId': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          // 已有数据（由 RosterPage 提前写入）则直接用，不再发请求覆盖
          if (this.store.rosterList && this.store.rosterList.length > 0) {
            this.rosterList = this.store.rosterList
          } else {
            this.loadRoster()
          }
        }
      },
      immediate: true
    },

    // 监听 rosterList 变化，自动保存（带防抖）
    rosterList: {
      handler() {
        if (this.store.currentDesignId) {
          if (this._saveTimer) clearTimeout(this._saveTimer)
          this._saveTimer = setTimeout(() => {
            // 有不完整的行时给予提示，但不阻止保存（saveToServer 内部只发完整行）
            const incomplete = this.rosterList.filter(member => {
              const nameEmpty = !member.name || member.name.trim() === ''
              const sizeEmpty = !member.size || member.size === ''
              const qtyEmpty  = member.quantity === undefined || member.quantity === null || member.quantity === ''
              return nameEmpty || sizeEmpty || qtyEmpty
            })
            if (incomplete.length > 0) {
              this.showMessage(`${incomplete.length} member(s) missing Name / Size / Qty`, 'warning')
              setTimeout(() => this.showMessage('', ''), 3000)
            }
            this.saveToServer()
          }, 500)
        }
      },
      deep: true
    }
  },
  methods: {
    addMember() {
      this.rosterList.push({
        name: '',
        gender: 'Male',
        style: this.store.currentGarment || 'Short Sleeve',
        size: '',        
        quantity: '',    
        message: ''
      })
     
    },

    loadFromStore() {
      if (this.store.rosterList && this.store.rosterList.length > 0) {
        this.rosterList = [...this.store.rosterList]
        console.log('从 store 加载 roster 成功，数量:', this.rosterList.length)
      } else {
        this.rosterList = []
      }
    },


  // 必填项空值红色样式判断
  getInputClass(val) {
    return { 'required-empty': !val || val.toString().trim() === '' }
  },
  // 输入时触发校验 + 仅三项都填完才自动保存
  onFieldChange(member) {
    // 防抖
    if (this._saveTimer) clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => {
      // 判断当前行三项是否全部填写
      const nameOk = member.name && member.name.trim()
      const sizeOk = member.size
      const qtyOk = member.quantity && member.quantity.toString().trim()
      // 只要有一项空 → 不保存
      if (!nameOk || !sizeOk || !qtyOk) return
      // 全部填完 → 执行自动保存
      this.saveToServer()
    }, 400)
  },


    // 检查所有成员的必填项是否完整
    isRosterComplete() {
      if (this.rosterList.length === 0) return true
      
      const incompleteMembers = this.rosterList.filter(member => {
        const nameEmpty = !member.name || member.name.trim() === ''
        const sizeEmpty = !member.size || member.size === ''
        const qtyEmpty = member.quantity === undefined || member.quantity === null || member.quantity === ''
        return nameEmpty || sizeEmpty || qtyEmpty
      })
      
      return incompleteMembers.length === 0
    },

    // 获取不完整的成员数量
    getIncompleteCount() {
      if (this.rosterList.length === 0) return 0
      
      return this.rosterList.filter(member => {
        const nameEmpty = !member.name || member.name.trim() === ''
        const sizeEmpty = !member.size || member.size === ''
        const qtyEmpty = member.quantity === undefined || member.quantity === null || member.quantity === ''
        return nameEmpty || sizeEmpty || qtyEmpty
      }).length
    },
    showDeleteModal(index) {
        const member = this.rosterList[index];
        const memberName = member.name || 'this member';
        
        // 显示弹窗并设置消息
        const modal = document.getElementById('deleteModal');
        const messageEl = document.getElementById('deleteModalMessage');
        messageEl.innerHTML = `Are you sure you want to remove <strong>${this.escapeHtml(memberName)}</strong> from the roster? This action cannot be undone.`;
        modal.style.display = 'flex';
        
        // 保存当前要删除的索引
        this.pendingDeleteIndex = index;
        
        // 绑定确认按钮
        const confirmBtn = document.getElementById('deleteModalConfirmBtn');
        const cancelBtn = document.getElementById('deleteModalCancelBtn');
        
        // 移除旧的监听器
        const newConfirmBtn = confirmBtn.cloneNode(true);
        const newCancelBtn = cancelBtn.cloneNode(true);
        confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);
        cancelBtn.parentNode.replaceChild(newCancelBtn, cancelBtn);
        
        // 确认删除
        newConfirmBtn.onclick = () => {
            if (this.pendingDeleteIndex !== null) {
                this.rosterList.splice(this.pendingDeleteIndex, 1);
                this.pendingDeleteIndex = null;
                this.saveToServer();
                this.showMessage('Member deleted', 'success');
                setTimeout(() => this.showMessage('', ''), 1500);
            }
            modal.style.display = 'none';
        };
        
        // 取消删除
        newCancelBtn.onclick = () => {
            this.pendingDeleteIndex = null;
            modal.style.display = 'none';
        };
        
        // 点击模态框背景关闭
        modal.onclick = (e) => {
            if (e.target === modal) {
                this.pendingDeleteIndex = null;
                modal.style.display = 'none';
            }
        };
    },

    escapeHtml(text) {
        if (!text) return '';
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    },

    async loadRosterWithRetry(retryCount = 0) {
      const maxRetries = 10
      const retryDelay = 300

      // store 里已经有数据（由 RosterPage.init() 提前写入），直接用，不再发网络请求
      if (this.store.rosterList && this.store.rosterList.length > 0) {
        this.rosterList = this.store.rosterList
        return
      }

      // 有 designId 则发请求
      if (this.store.currentDesignId) {
        await this.loadRoster()
        return
      }

      // 直接从 URL ?id= 读取（独立打开 roster 链接时 store 可能还没初始化）
      const urlId = new URLSearchParams(window.location.search).get('id')
      if (urlId) {
        this.store.currentDesignId = urlId
        await this.loadRoster()
        return
      }

      // 都没有则重试等待
      if (retryCount < maxRetries) {
        setTimeout(() => this.loadRosterWithRetry(retryCount + 1), retryDelay)
      } else {
        console.log('加载队员名单超时：未获取到 designId')
      }
    },

    async saveMember(index) {
      // 手动保存当前队员数据
      if (!this.store.currentDesignId) {
        this.showMessage('请先保存设计', 'error')
        setTimeout(() => this.showMessage('', ''), 2000)
        return
      }
      
      try {
        const success = await this.saveToServer()
        if (success) {
          this.showMessage('已保存', 'success')
        } else {
          this.showMessage('保存失败', 'error')
        }
        setTimeout(() => this.showMessage('', ''), 1500)
      } catch (error) {
        console.error('保存失败:', error)
        this.showMessage('保存失败', 'error')
        setTimeout(() => this.showMessage('', ''), 2000)
      }
    },
    removeMember(index) {
      if (confirm('确定删除该队员吗？')) {
        this.rosterList.splice(index, 1)
      }
    },
    async saveToServer() {
      const designId = this.store.currentDesignId
      if (!designId) {
        console.log('无 designId，跳过保存')
        return false
      }
      
      // 只保存三项都填写完整的行，不完整的行跳过（不阻止整体保存）
      const rosterData = this.rosterList
        .filter(member => {
          const nameOk = member.name && member.name.trim() !== ''
          const sizeOk = member.size && member.size !== ''
          const qtyOk  = member.quantity !== undefined && member.quantity !== null && member.quantity !== ''
          return nameOk && sizeOk && qtyOk
        })
        .map(member => ({
          name:     member.name.trim(),
          gender:   member.gender  || 'Male',
          style:    member.style   || this.store.currentGarment || 'Short Sleeve',
          size:     member.size,
          quantity: Number(member.quantity),
          message:  member.message || ''
        }))
      
      try {
        const response = await fetch('/save-roster.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            design_id: designId,
            roster: rosterData
          })
        })
        const result = await response.json()
        if (result.success) {
          console.log('Roster saved')
          this.showMessage('Saved', 'success')
          setTimeout(() => this.showMessage('', ''), 1500)
          return true
        } else {
          console.error('Save failed:', result)
          this.showMessage('Save failed', 'error')
          setTimeout(() => this.showMessage('', ''), 2000)
          return false
        }
      } catch (e) {
        console.error('Save failed', e)
        this.showMessage('Save failed', 'error')
        setTimeout(() => this.showMessage('', ''), 2000)
        return false
      }
    },
    async loadRoster() {
      const designId = this.store.currentDesignId
      if (!designId) {
        console.log('无 designId，无法加载队员名单')
        return false
      }
      
      console.log('加载队员名单，designId:', designId)
      
      try {
        const response = await fetch(`/load-roster.php?design_id=${designId}`)
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        const result = await response.json()
        if (result.success && result.roster) {
          // ★ 保留原始值，不强制转换空值为 M 或 1
          this.rosterList = result.roster.map(member => ({
            name: member.name || '',
            gender: member.gender || 'Male',
            style: member.style || this.store.currentGarment || 'Short Sleeve',
            size: member.size === undefined || member.size === null ? '' : member.size,
            quantity: (member.quantity === undefined || member.quantity === null || member.quantity === '') ? '' : member.quantity,
            message: member.message || ''
          }))
          console.log('加载成功，队员数量:', this.rosterList.length)
          return true
        } else {
          this.rosterList = []
          console.log('无队员数据或加载失败')
          return false
        }
      } catch (e) {
        console.error('加载失败', e)
        this.rosterList = []
        return false
      }
    },

    showMessage(msg, type) {
      this.saveMessage = msg
      this.saveMessageType = type
    }
  },
  beforeUnmount() {
    // 清除防抖定时器，避免组件销毁后仍然执行保存操作
    if (this.saveTimer) {
      clearTimeout(this.saveTimer)
      this.saveTimer = null
    }
  }
}
</script>

<style scoped>
.roster-table-wrapper {
  max-height: calc(100vh - 320px);  /* 视口高度减去页面其他元素高度 */
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-top: 8px;
}
.roster-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}
.roster-table th,
.roster-table td {
  padding: 8px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
  vertical-align: middle;
}
/* Name 输入框样式 */
.name-input {
  min-width: 100px;
}

/* Quantity 输入框样式 */
.quantity-input {
  width: 50px;
  text-align: center;
  padding: 6px 1px;
}

/* Notes 输入框样式 */
.notes-input {
  width: 100%;
  padding: 10px 4px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.75rem;
  box-sizing: border-box;
  background: #f8fff5;
}

/* Delete 按钮样式 - 长方形 */
button.delete-btn {
  width: 80px;
  min-width: 80px;
  height: auto;
  background: #ef4444;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: background 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

button.delete-btn:hover {
  background: #dc2626;
}

.delete-btn {
  width: 80px;
  background: #ef4444;
  border: none;
  color: white;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  transition: background 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  background: #dc2626;
}
.operate-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
}

/* Save 按钮样式 */
.save-btn {
  background: #10b981;
}

.save-btn:hover {
  background: #059669;
}

/* 备注行占位单元格样式（无边框、无内边距或极小） */
.notes-placeholder {
  padding: 0 !important;
  border: none !important;
}

/* 或者保留最小宽度 */
.notes-placeholder {
  width: 15%; /* 与 Name 列宽度一致 */
  padding: 0;
  border: none;
}

/* 数据行底部边框 */
.data-row td {
  border-bottom: none;
}

.notes-row td {
  border-top: none;
  border-bottom: 1px solid #e0e0e0;
}

.notes-row:last-child td {
  border-bottom: 1px solid #e0e0e0;
}

/* 保持原有 danger 按钮样式 */
.icon-btn.danger {
  background: #ef4444;
}
.icon-btn.danger:hover {
  background: #dc2626;
}
/* Operate 单元格样式 */
.operate-cell {
  vertical-align: middle;
}
.roster-table th {
  background: #f5f5f5;
  position: sticky;
  top: 0;
  font-weight: 600;
}
.roster-input {
  width: 100%;
  padding: 6px 1px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.75rem;
  box-sizing: border-box;
}
.roster-select {
  width: 100%;
  padding: 6px 1px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.75rem;
  background: white;
}
.empty-row {
  text-align: center;
  color: #999;
  padding: 20px;
}
.roster-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  flex-wrap: wrap;
  gap: 10px;
}
.add-member-btn {
  background: #e0e7ff;
  border: none;
  color: #3730a3;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
}
.add-member-btn:hover {
  background: #c7d2fe;
}
.roster-stats {
  font-size: 0.75rem;
  color: #666;
  background: #f0f0f0;
  padding: 6px 12px;
  border-radius: 16px;
}
.icon-btn {
  background: #2d3748;
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}
.icon-btn.danger {
  background: #ef4444;
}
.icon-btn.danger:hover {
  background: #dc2626;
}
.save-status {
  margin-top: 8px;
  font-size: 0.7rem;
  text-align: center;
  padding: 4px;
  border-radius: 4px;
}
.save-status.success {
  color: #10b981;
  background: #d1fae5;
}
.save-status.error {
  color: #ef4444;
  background: #fee2e2;
}
.save-status.warning {
  color: #f59e0b;
  background: #fef3c7;
}

/* 自定义删除弹窗样式 - 与 TextEditor 一致 */
.delete-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 20000;
}

.delete-modal-content {
    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
    border-radius: 24px;
    width: 90%;
    max-width: 400px;
    padding: 2rem 1.8rem 1.8rem;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
    border: 1px solid rgba(59, 130, 246, 0.5);
    animation: modalFadeIn 0.2s ease-out;
    position: relative;
}

.delete-modal-header {
    padding: 0;
    margin-bottom: 0.5rem;
}

.delete-modal-header h3 {
    color: #ffffff;
    font-size: 1.4rem;
    font-weight: 600;
    margin: 0;
    letter-spacing: -0.3px;
}

.delete-modal-body {
    padding: 0;
    margin-bottom: 1.5rem;
}

.delete-modal-body p {
    color: #94a3b8;
    font-size: 0.9rem;
    line-height: 1.5;
    margin: 0;
}

.delete-modal-footer {
    padding: 0;
    display: flex;
    gap: 12px;
    justify-content: flex-end;
}

.delete-modal-btn {
    flex: 1;
    padding: 10px 0;
    border-radius: 40px;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    border: none;
    transition: all 0.2s;
    font-family: inherit;
}

.delete-modal-btn.cancel {
    background: #334155;
    color: #cbd5e1;
}

.delete-modal-btn.cancel:hover {
    background: #475569;
}

.delete-modal-btn.confirm {
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
}

.delete-modal-btn.confirm:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
}

/* 必填为空红色提醒样式 */
.roster-input.required-empty,
.roster-select.required-empty {
  border: 1px solid #ef4444 !important;
  background-color: #fef2f2 !important;
}


@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: scale(0.95) translateY(-10px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

@media (max-width: 850px) {
  .roster-table th,
  .roster-table td {
    padding: 4px;
    font-size: 0.7rem;
  }
  .roster-input,
  .roster-select {
    padding: 4px;
    font-size: 0.7rem;
  }
  .add-member-btn {
    padding: 6px 12px;
  }
}
</style>